import * as React from "react";
import { Segment, Selection } from "core/models";

const TIMESTAMPS_PREFERENCE_KEY = "media.spot-on.showTimestamps";
const DURATIONS_PREFERENCE_KEY = "media.spot-on.showDurations";

type EditorContextType = {
    selections: Selection[];
    currentSelectionID: string | null;
    showTimestamps: boolean;
    showDurations: boolean;
    thumbnailURL: string | null;
    thumbnailTime: number;
    chunkContainerDisplayDate: Date;
    channelId: number;
    setChunkContainerDisplayDate(newDate: Date): void;
    chunkContainerNavigationDate?: Date;
    setChunkContainerNavigationDate(newDate: Date | undefined): void;
    setSelections(newSelections: Selection[]): void;
    setShowDurations(value: boolean): void;
    setShowTimestamps(value: boolean): void;
    setCurrentSelectionID(value: string): void;
    setThumbnailURL(value: string | null): void;
    setThumbnailTime(value: number): void;
    setChannelId(value: number): void;
    selectionsDuration: number;
    segmentsCache: Segment[];
    setSegmentsCache(segments: Segment[]): void;
    liveMode: boolean;
    setLiveMode(value: boolean): void;
    previewing: boolean;
    setPreviewing(balue: boolean): void;
};

const EditorContext = React.createContext<EditorContextType | null>(null);

const useEditorContext = (): EditorContextType => {
    const context = React.useContext(EditorContext);
    if (!context) {
        throw new Error("Unable to create EditorContext");
    }
    return context;
};

function getPreference(key: string): boolean {
    const pref = localStorage.getItem(key) === "true";
    return pref;
}

function setPreference(key: string, value: boolean) {
    localStorage.setItem(key, value === true ? "true" : "false");
}

function useThumbnailURL() {
    const [thumbnailURL, setThumbnailURL] = React.useState<string | null>(null);

    React.useEffect(
        () => () => {
            if (thumbnailURL) {
                URL.revokeObjectURL(thumbnailURL);
            }
        },
        [thumbnailURL],
    );

    return {
        thumbnailURL,
        setThumbnailURL,
    };
}

const getSelectionsDuration = (selections: Selection[]): number =>
    selections.reduce((grandTotal, currentSelection) => {
        const totalTime = currentSelection.segments.reduce(
            (accumulator, currentSegment) =>
                accumulator + currentSegment.tsLength,
            0,
        );
        return grandTotal + totalTime;
    }, 0);

type Props = {
    children: React.ReactNode;
};

const EditorContextProvider: React.FC<Props> = ({ children }) => {
    const [selections, setSelections] = React.useState<Selection[]>([
        new Selection(),
    ]);
    const [showTimestamps, setShowTimestamps] = React.useState<boolean>(
        getPreference(TIMESTAMPS_PREFERENCE_KEY) || true,
    );
    const [previewing, setPreviewing] = React.useState<boolean>(false);
    const [showDurations, setShowDurations] = React.useState<boolean>(
        getPreference(DURATIONS_PREFERENCE_KEY) || true,
    );
    const [currentSelectionID, setCurrentSelectionID] = React.useState<string>(
        selections[0].id,
    );
    const [chunkContainerNavigationDate, setChunkContainerNavigationDate] =
        React.useState<Date | undefined>(undefined);
    const [chunkContainerDisplayDate, setChunkContainerDisplayDate] =
        React.useState<Date>(new Date());
    const [channelId, setChannelId] = React.useState<number>(1);
    const [selectionsDuration, setSelectionsDuration] =
        React.useState<number>(0);
    const [segmentsCache, setSegmentsCache] = React.useState<Segment[]>([]);
    const [liveMode, setLiveMode] = React.useState<boolean>(false);

    const [thumbnailTime, setThumbnailTime] = React.useState<number>(0);
    const { thumbnailURL, setThumbnailURL } = useThumbnailURL();

    const context: EditorContextType = {
        selections,
        currentSelectionID,
        showTimestamps,
        showDurations,
        thumbnailURL,
        thumbnailTime,
        chunkContainerDisplayDate,
        channelId,
        setChunkContainerDisplayDate,
        chunkContainerNavigationDate,
        setChunkContainerNavigationDate,
        setSelections: (newSelections: Selection[]) => {
            setPreviewing(false);
            setSelectionsDuration(getSelectionsDuration(newSelections));
            setSelections(newSelections);
        },
        setShowDurations: (newValue: boolean) => {
            setPreference(DURATIONS_PREFERENCE_KEY, newValue);
            setShowDurations(newValue);
        },
        setShowTimestamps: (newValue: boolean) => {
            setPreference(TIMESTAMPS_PREFERENCE_KEY, newValue);
            setShowTimestamps(newValue);
        },
        setCurrentSelectionID,
        setThumbnailTime,
        setThumbnailURL,
        setChannelId,
        selectionsDuration,
        segmentsCache,
        setSegmentsCache,
        liveMode,
        setLiveMode,
        previewing,
        setPreviewing: (newValue: boolean) => {
            setPreviewing(newValue);
        },
    };

    return (
        <EditorContext.Provider value={context}>
            {children}
        </EditorContext.Provider>
    );
};

export default EditorContext;
export { useEditorContext, EditorContextProvider };
